import { useEffect, useId, useMemo, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, Button, useActionDialog } from '../../../../components/ui';
import type {
  CycleRef,
  PlanMeshRole,
  PlanSchemeSlot,
} from '../../../../api/plan-options';
import type {
  JawType,
  MeshAsset,
  MeshRole,
  PlanOption,
  PlanScheme,
  TreatmentStage,
} from '../../../../types/admin';
import {
  useAppendClinicalElementScheme,
  useAppendPlanScheme,
  useDeletePlanOption,
  useDeletePlanOptionMesh,
  useDeletePlanSchemeAsset,
  usePublishPlanOption,
  useReprocessMesh,
  useSetPlanSelection,
  useUpdatePlanOption,
  useUploadPlanOptionMesh,
} from '../../hooks/usePlanOptions';
import {
  draftToPayload,
  isDraftDirty,
  manufacturedThisStage,
  toDraft,
  validatePlanDraft,
  type PlanDraft,
} from './draft';
import { MeshUploadSlot } from './MeshUploadSlot';
import { MeshViewerModalLazy } from './MeshViewerModalLazy';
import { PlanFieldsEditor, type PlanSchemeTarget } from './PlanFieldsEditor';
import styles from './PlanOptions.module.scss';
import { PlanTotalSummary } from './PlanTotalSummary';
import { computeStructuredPlanTotal, cyclePricingInputs } from './pricing';
import { SchemeImageControl } from './SchemeImageControl';

const MESH_CELLS: readonly { readonly role: PlanMeshRole; readonly jaw: JawType }[] = [
  { role: 'current', jaw: 'upper' },
  { role: 'result', jaw: 'upper' },
  { role: 'current', jaw: 'lower' },
  { role: 'result', jaw: 'lower' },
];

function cellKey(role: MeshRole, jaw: JawType): string {
  return `${role}:${jaw}`;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Unexpected error';
}

function optionFromStage(stage: TreatmentStage, optionId: string): PlanOption | null {
  return stage.plan_options.find((entry) => entry.id === optionId) ?? null;
}

function schemesFromBlock(
  block: { readonly scheme?: PlanScheme | null; readonly schemes?: readonly PlanScheme[] } | null | undefined,
): readonly PlanScheme[] {
  return block?.schemes?.length ? block.schemes : block?.scheme ? [block.scheme] : [];
}

function schemeIds(schemes: readonly PlanScheme[]): string {
  return schemes.map((scheme) => scheme.id).join('|');
}

export type PlanOptionCardProps = {
  readonly cycle: CycleRef;
  readonly stage: TreatmentStage;
  readonly option: PlanOption;
  readonly displayTitle: string;
  /** The main plan cannot be removed while alternatives still exist. */
  readonly canDelete: boolean;
  /** Hidden editors stay mounted to preserve drafts, but must not fetch image bytes. */
  readonly isActive?: boolean;
  /** Reported upward so the plan rail can flag a hidden editor as unsaved. */
  readonly onDirtyChange?: (isDirty: boolean) => void;
};

export function PlanOptionCard({
  cycle,
  stage,
  option,
  displayTitle,
  canDelete,
  isActive = true,
  onDirtyChange,
}: PlanOptionCardProps) {
  const { t } = useTranslation();
  const { confirmAction } = useActionDialog();
  const publishHintId = useId();

  const updateMutation = useUpdatePlanOption(cycle);
  const deleteMutation = useDeletePlanOption(cycle);
  const publishMutation = usePublishPlanOption(cycle);
  const selectionMutation = useSetPlanSelection(cycle);
  const uploadMeshMutation = useUploadPlanOptionMesh(cycle);
  const deleteMeshMutation = useDeletePlanOptionMesh(cycle);
  const reprocessMutation = useReprocessMesh(cycle);
  const appendSchemeMutation = useAppendPlanScheme(cycle);
  const appendClinicalSchemeMutation = useAppendClinicalElementScheme(cycle);
  const deleteSchemeAssetMutation = useDeletePlanSchemeAsset(cycle);

  const [draft, setDraft] = useState<PlanDraft>(() => toDraft(option));
  const [isViewerOpen, setViewerOpen] = useState(false);
  // Only the newest failure is shown; a fresh attempt clears it. Concatenating
  // every mutation's `error` kept stale messages on screen indefinitely.
  const [lastError, setLastError] = useState<string | null>(null);
  // Keys of the slots with a request in flight, so one upload does not freeze
  // the other three cells.
  const [busySlots, setBusySlots] = useState<ReadonlySet<string>>(() => new Set());
  const previousOptionRef = useRef(option);

  // Refetches may carry scheme mutations completed in this tab or another admin
  // session. Reconcile only read-only asset metadata; never replace typed fields.
  useEffect(() => {
    const previousOption = previousOptionRef.current;
    previousOptionRef.current = option;
    if (previousOption === option) return;

    setDraft((previous) => {
      let changed = false;
      const reconcileFixed = <T extends { readonly scheme: PlanScheme | null; readonly schemes: readonly PlanScheme[] }>(
        block: T,
        previousBlock: { readonly scheme?: PlanScheme | null; readonly schemes?: readonly PlanScheme[] } | null | undefined,
        nextBlock: { readonly scheme?: PlanScheme | null; readonly schemes?: readonly PlanScheme[] } | null | undefined,
      ): T => {
        const previousSchemes = schemesFromBlock(previousBlock);
        const nextSchemes = schemesFromBlock(nextBlock);
        if (schemeIds(previousSchemes) === schemeIds(nextSchemes)) return block;
        changed = true;
        return { ...block, scheme: nextBlock?.scheme ?? nextSchemes.at(-1) ?? null, schemes: nextSchemes };
      };
      const clinicalElements = previous.clinicalElements.map((element) => {
        const previousElement = previousOption.clinical_elements?.find(
          (entry) => entry.id === element.id,
        );
        const nextElement = option.clinical_elements?.find((entry) => entry.id === element.id);
        if (
          !nextElement ||
          schemeIds(schemesFromBlock(previousElement)) === schemeIds(schemesFromBlock(nextElement))
        ) {
          return element;
        }
        changed = true;
        const schemes = schemesFromBlock(nextElement);
        return { ...element, scheme: nextElement.scheme ?? schemes.at(-1) ?? null, schemes };
      });
      const attachments = reconcileFixed(
        previous.attachments,
        previousOption.attachments,
        option.attachments,
      );
      const buttons = reconcileFixed(
        previous.buttons,
        previousOption.buttons,
        option.buttons,
      );
      const elastics = reconcileFixed(
        previous.elastics,
        previousOption.elastics,
        option.elastics,
      );
      const separation = reconcileFixed(
        previous.separation,
        previousOption.separation,
        option.separation,
      );
      return changed
        ? { ...previous, attachments, buttons, elastics, separation, clinicalElements }
        : previous;
    });
  }, [option]);

  function patchDraft(patch: Partial<PlanDraft>) {
    setDraft((previous) => ({ ...previous, ...patch }));
  }

  function patchFixedSchemes(slot: PlanSchemeSlot, schemes: readonly PlanScheme[]) {
    setDraft((previous) => ({
      ...previous,
      [slot]: { ...previous[slot], scheme: schemes.at(-1) ?? null, schemes },
    }));
  }

  function patchClinicalSchemes(elementId: string, schemes: readonly PlanScheme[]) {
    setDraft((previous) => ({
      ...previous,
      clinicalElements: previous.clinicalElements.map((element) =>
        element.id === elementId ? { ...element, scheme: schemes.at(-1) ?? null, schemes } : element,
      ),
    }));
  }

  const mutationCallbacks = {
    onError: (error: unknown) => setLastError(errorMessage(error)),
    onSuccess: () => setLastError(null),
  };

  function markSlot(key: string, busy: boolean) {
    setBusySlots((previous) => {
      const next = new Set(previous);
      if (busy) next.add(key);
      else next.delete(key);
      return next;
    });
  }

  function runSlotMutation(key: string, run: (settle: () => void) => void) {
    setLastError(null);
    markSlot(key, true);
    run(() => markSlot(key, false));
  }

  async function runSchemeMutation(key: string, run: () => Promise<void>) {
    setLastError(null);
    markSlot(key, true);
    try {
      await run();
      setLastError(null);
    } catch (error: unknown) {
      setLastError(errorMessage(error));
    } finally {
      markSlot(key, false);
    }
  }

  const pricing = useMemo(() => cyclePricingInputs(stage), [stage]);
  const breakdown = useMemo(() => computeStructuredPlanTotal(draft), [draft]);
  const currency = option.pricing_breakdown?.currency ?? pricing.currency;
  const validationIssues = useMemo(() => validatePlanDraft(draft), [draft]);
  const publishDisabledReason =
    isDraftDirty(draft, option) || validationIssues.length > 0
      ? t('planOptions.publishNeedsSavedValid')
      : null;

  const meshByCell = useMemo(() => {
    const map = new Map<string, MeshAsset>();
    (option.meshes ?? []).forEach((mesh) => map.set(cellKey(mesh.role, mesh.jaw), mesh));
    return map;
  }, [option.meshes]);

  const viewerMeshes = useMemo(() => option.meshes ?? [], [option.meshes]);
  const isDirty = useMemo(() => isDraftDirty(draft, option), [draft, option]);
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);
  const hasCounts = manufacturedThisStage(draft) > 0;
  const isPlanActionBusy =
    updateMutation.isPending ||
    deleteMutation.isPending ||
    publishMutation.isPending ||
    selectionMutation.isPending;
  const isCardBusy =
    isPlanActionBusy ||
    appendSchemeMutation.isPending ||
    appendClinicalSchemeMutation.isPending ||
    deleteSchemeAssetMutation.isPending ||
    busySlots.size > 0;

  function renderSchemeControl(target: PlanSchemeTarget) {
    const key =
      target.kind === 'slot'
        ? `scheme:${target.slot}`
        : `scheme:clinical:${target.elementId}`;

    return (
      <SchemeImageControl
        cycle={cycle}
        optionId={option.id}
        label={target.label}
        schemes={target.schemes}
        isVisible={isActive}
        isDirty={isDirty}
        isBusy={busySlots.has(key)}
        isDisabled={isPlanActionBusy}
        onSelectFiles={(files) => {
          void runSchemeMutation(key, async () => {
            let updatedStage: TreatmentStage | null = null;
            for (const file of files) {
              updatedStage =
                target.kind === 'slot'
                  ? await appendSchemeMutation.mutateAsync({
                      optionId: option.id,
                      slot: target.slot,
                      file,
                    })
                  : await appendClinicalSchemeMutation.mutateAsync({
                      optionId: option.id,
                      elementId: target.elementId,
                      file,
                    });
            }
            if (!updatedStage) return;
            const updatedOption = optionFromStage(updatedStage, option.id);
            if (target.kind === 'slot') {
              patchFixedSchemes(target.slot, schemesFromBlock(updatedOption?.[target.slot]));
            } else {
              const block = updatedOption?.clinical_elements?.find(
                (element) => element.id === target.elementId,
              );
              patchClinicalSchemes(target.elementId, schemesFromBlock(block));
            }
          });
        }}
        onDelete={(scheme) => {
          void runSchemeMutation(key, async () => {
            const updatedStage = await deleteSchemeAssetMutation.mutateAsync({
              optionId: option.id,
              schemeId: scheme.id,
            });
            const updatedOption = optionFromStage(updatedStage, option.id);
            if (target.kind === 'slot') {
              patchFixedSchemes(target.slot, schemesFromBlock(updatedOption?.[target.slot]));
            } else {
              const block = updatedOption?.clinical_elements?.find(
                (element) => element.id === target.elementId,
              );
              patchClinicalSchemes(target.elementId, schemesFromBlock(block));
            }
          });
        }}
      />
    );
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (validationIssues.length > 0) {
      setLastError(t('planOptions.validationFix'));
      return;
    }
    setLastError(null);
    try {
      await updateMutation.mutateAsync({ optionId: option.id, payload: draftToPayload(draft) });
    } catch (error: unknown) {
      setLastError(errorMessage(error));
    }
  }

  async function handleDelete() {
    const confirmed = await confirmAction({
      message: t('planOptions.confirmDelete', { title: displayTitle }),
      confirmLabel: t('dialog.delete'),
      tone: 'danger',
    });
    if (confirmed) {
      setLastError(null);
      deleteMutation.mutate(option.id, mutationCallbacks);
    }
  }

  const selectionDisabledReason = !option.is_published
    ? t('planOptions.selectNeedsPublished')
    : isDirty || validationIssues.length > 0
      ? t('planOptions.publishNeedsSavedValid')
      : !hasCounts
        ? t('planOptions.selectNeedsCounts')
        : null;

  return (
    <section
      className={`${styles.card} ${option.is_selected ? styles.cardSelected : ''}`}
      aria-label={displayTitle}
      data-testid="plan-option-card"
    >
      <header className={styles.cardHeader}>
        <div className={styles.cardHeaderMain}>
          <h5 className={styles.cardTitle}>{displayTitle}</h5>
          <div className={styles.badgeRow}>
            {option.is_main && (
              <Badge variant="accent" size="sm">
                {t('planOptions.mainBadge')}
              </Badge>
            )}
            {option.is_selected && (
              <Badge variant="success" size="sm">
                {t('planOptions.selectedBadge')}
              </Badge>
            )}
            <Badge variant={option.is_published ? 'info' : 'neutral'} size="sm">
              {option.is_published ? t('planOptions.published') : t('planOptions.unpublished')}
            </Badge>
            {isDirty && (
              <span className={styles.dirtyChip} data-testid="plan-dirty">
                {t('planOptions.unsavedChanges')}
              </span>
            )}
          </div>
        </div>
        <div className={styles.cardActions}>
          <label className={styles.publishToggle}>
            <input
              type="checkbox"
              checked={option.is_published}
              disabled={isCardBusy || isDirty || validationIssues.length > 0}
              title={publishDisabledReason ?? undefined}
              aria-describedby={publishDisabledReason ? publishHintId : undefined}
              onChange={(event) => {
                setLastError(null);
                publishMutation.mutate(
                  { optionId: option.id, isPublished: event.target.checked },
                  mutationCallbacks,
                );
              }}
            />
            {t('planOptions.showToPatient')}
          </label>
          {publishDisabledReason && (
            <span id={publishHintId} className={styles.publishHint}>
              {publishDisabledReason}
            </span>
          )}
          {option.is_selected ? (
            <Button
              size="sm"
              variant="secondary"
              disabled={isCardBusy}
              onClick={() => {
                setLastError(null);
                selectionMutation.mutate(null, mutationCallbacks);
              }}
            >
              {t('planOptions.resetSelection')}
            </Button>
          ) : (
            <Button
              size="sm"
              variant="secondary"
              disabled={isCardBusy || selectionDisabledReason !== null}
              title={selectionDisabledReason ?? undefined}
              onClick={() => {
                setLastError(null);
                selectionMutation.mutate(option.id, mutationCallbacks);
              }}
            >
              {t('planOptions.selectForPatient')}
            </Button>
          )}
          {canDelete && (
            <Button size="sm" variant="danger" disabled={isCardBusy} onClick={handleDelete}>
              {t('dialog.delete')}
            </Button>
          )}
        </div>
      </header>

      <div className={styles.cardBody}>
        <form onSubmit={handleSubmit} className={styles.cardForm}>
          {validationIssues.length > 0 && (
            <div className={styles.validationSummary} role="alert">
              {validationIssues.map((issue) => (
                <p key={issue}>{t(`planOptions.validation_${issue}`)}</p>
              ))}
            </div>
          )}
          <PlanFieldsEditor
            draft={draft}
            isMain={option.is_main}
            currency={currency}
            onChange={patchDraft}
            renderSchemeControl={renderSchemeControl}
          />

          <div className={styles.footerBar}>
            {lastError && <p className={styles.inlineError}>{lastError}</p>}
            {isDirty && !updateMutation.isPending && (
              <span className={styles.footerHint}>{t('planOptions.unsavedHint')}</span>
            )}
            <Button type="submit" variant="primary" size="sm" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? t('planOptions.saving') : t('planOptions.savePlan')}
            </Button>
          </div>
        </form>

        <aside className={styles.cardSide}>
          <PlanTotalSummary breakdown={breakdown} currency={currency} />

          <div className={styles.meshBlock}>
            <div className={styles.meshHeader}>
              <span className={styles.itemsLabel}>{t('planOptions.meshSection')}</span>
              <Button
                size="sm"
                variant="secondary"
                disabled={viewerMeshes.length === 0}
                onClick={() => setViewerOpen(true)}
              >
                {t('planOptions.view3d')}
              </Button>
            </div>
            <div className={styles.meshGrid}>
              {MESH_CELLS.map(({ role, jaw }) => {
                const key = cellKey(role, jaw);
                const mesh = meshByCell.get(key) ?? null;
                return (
                  <MeshUploadSlot
                    key={key}
                    label={`${t(`planOptions.jaw_${jaw}`)} · ${t(`planOptions.role_${role}`)}`}
                    mesh={mesh}
                    isBusy={busySlots.has(key)}
                    onSelectFile={(file) =>
                      runSlotMutation(key, (settle) =>
                        uploadMeshMutation.mutate(
                          { optionId: option.id, role, jaw, file },
                          { ...mutationCallbacks, onSettled: settle },
                        ),
                      )
                    }
                    onDelete={() =>
                      runSlotMutation(key, (settle) =>
                        deleteMeshMutation.mutate(
                          { optionId: option.id, role, jaw },
                          { ...mutationCallbacks, onSettled: settle },
                        ),
                      )
                    }
                    onReprocess={
                      mesh
                        ? () =>
                            runSlotMutation(key, (settle) =>
                              reprocessMutation.mutate(mesh.id, {
                                ...mutationCallbacks,
                                onSettled: settle,
                              }),
                            )
                        : undefined
                    }
                  />
                );
              })}
            </div>
          </div>
        </aside>
      </div>

      {isViewerOpen && (
        <MeshViewerModalLazy
          open={isViewerOpen}
          onClose={() => setViewerOpen(false)}
          title={displayTitle}
          cycle={cycle}
          meshes={viewerMeshes}
        />
      )}
    </section>
  );
}
